import { useEffect, useRef } from 'react'

import { useSession } from 'next-auth/react'
import { useRouter } from 'next/router'
import posthog from 'posthog-js'

import { useNewSurvey } from '@context/new-survey/useNewSurvey'
import useBrazeIntegration from '@context/useBrazeIntegration'
import { upgradeClaritySession } from '@lib/analytics/clarity'
import { trackGetStartedEvent } from '@lib/analytics/get-started-tracking'

import GetStartedPlansConfig from '@config/get-started-plans-config'

import type { GetStartedPlansConfig as GetStartedPlansConfigType } from '@lib/types'
import type { AnnouncementConfig } from '@lib/new-survey/get-new-survey'
import type { GetStartedOffer } from '@lib/types/get-started-offer'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'

import Logo from '@components/global/Logo'
import Auth from '@components/profile/Auth'

type GetStartedAuthProps = {
	onComplete: () => void
	announcement?: AnnouncementConfig | null
	offer?: GetStartedOffer | null
	pageContent?: GetStartedPageContent
}

export default function GetStartedAuth({ onComplete, announcement, offer, pageContent: pageContentProp }: GetStartedAuthProps) {
	const router = useRouter()
	const pageContent = pageContentProp ?? FALLBACK_PAGE_CONTENT
	const { data: session, status } = useSession()
	const hasCalledComplete = useRef(false)
	const { brazeFunction } = useBrazeIntegration()
	const { surveyQuestions } = useNewSurvey() || {}
	const config = GetStartedPlansConfig as GetStartedPlansConfigType
	const questionCount = surveyQuestions?.length || 0
	const currentStep = questionCount + 1 // auth is the step right after survey questions
	const totalSteps = questionCount + 2 + (config.checkoutStep.enabled ? 1 : 0)

	useEffect(() => {
		if (status === 'authenticated' && !hasCalledComplete.current) {
			hasCalledComplete.current = true
			// Link this browser session (with experiment flag) to email for purchase attribution on thank-you page
			const email = session?.user?.email
			if (email) {
				posthog.identify(email, {
					email,
					first_name: (session?.user as any)?.customer?.firstName,
					last_name: (session?.user as any)?.customer?.lastName
				})
			}
			trackGetStartedEvent('account_created')
			upgradeClaritySession('get_started_account_created')
			brazeFunction().then((braze) => {
				if (!braze) return
				braze.logCustomEvent('get_started_authenticated', { source: 'get_started_flow' })
				braze.requestImmediateDataFlush()
			})
			onComplete()
		}
	}, [status, session?.user, onComplete])

	if (status === 'authenticated') return null

	return (
		<div className="flex flex-col h-[100dvh] bg-white md:bg-transparent">
			{/* Fixed top: Logo + announcement + progress */}
			<div className="flex-shrink-0 bg-white">
				<div className="container">
					{/* Logo bar */}
					<div className="flex justify-center py-3 md:py-5">
						<button
							type="button"
							aria-label="Birchbox Home"
							className="cursor-pointer"
							onClick={() => {
								if (typeof window !== 'undefined' && window.history.length > 1) {
									router.back()
								} else {
									router.push('/')
								}
							}}
						>
							<Logo className="w-28 md:w-32 fill-bbx-style" />
						</button>
					</div>
				</div>

				{/* Offer teaser — reveal happens after signup on plan selection */}
				{offer && (
					<div className="bg-bbx-spark text-white text-center py-2.5 px-4 text-base font-semibold">
						{offer.authTeaser}
					</div>
				)}

				<div>
					{/* Progress row */}
					<div className="flex flex-wrap items-center gap-3 px-4 pt-3 pb-1 md:px-12 md:pt-6 max-w-4xl mx-auto w-full">
						<span className="text-sm text-bbx-style whitespace-nowrap">
							{currentStep} of {totalSteps}
						</span>
						<div className="relative flex-1 h-4 overflow-hidden bg-bbx-glimmer-light border-[3px] border-bbx-glimmer-light">
							<div
								className="absolute inset-y-0 left-0 h-2.5 transition-all duration-[800ms] ease-in-out bg-bbx-glimmer"
								style={{ width: `${(currentStep / totalSteps) * 100}%` }}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Scrollable content area */}
			<div className="flex-1 overflow-y-auto bg-white container">
				<div className="flex flex-col items-center px-4 py-6 md:px-12 md:py-10 max-w-4xl mx-auto w-full md:min-h-full md:justify-center">
					<h2 className="text-2xl md:text-3xl font-serif font-medium text-center">{pageContent.authHeading}</h2>
					<p className="mt-2 mb-8 text-sm md:text-base font-medium text-center text-gray-500">
						{pageContent.authSubtext}
					</p>
					<Auth step="sign-up" subscriptionBox />
				</div>
			</div>
		</div>
	)
}
